import { Box, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCaptchaConfigQuery } from '../../../features/auth/api/authApi';
import { loadSmartCaptchaScript } from '../../utils/authToken';
import { authErrorSx } from './authDialogStyles';

const SmartCaptcha = ({ onToken, resetKey = 0, enabled = true }) => {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const onTokenRef = useRef(onToken);
    const [loadError, setLoadError] = useState(null);

    const envSiteKey = process.env.REACT_APP_SMARTCAPTCHA_CLIENT_KEY || '';
    const { data: captchaConfig } = useGetCaptchaConfigQuery(undefined, {
        skip: Boolean(envSiteKey) || !enabled,
    });
    const siteKey = envSiteKey || captchaConfig?.site_key || '';
    const isValidClientKey = /^ysc1_[A-Za-z0-9_-]+$/.test(siteKey);

    onTokenRef.current = onToken;

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        if (!siteKey) {
            if (process.env.NODE_ENV === 'development') {
                onTokenRef.current?.('dev-captcha-token');
            } else {
                onTokenRef.current?.('');
            }
            return undefined;
        }

        if (!isValidClientKey) {
            onTokenRef.current?.('');
            return undefined;
        }

        let cancelled = false;

        const renderCaptcha = async () => {
            setLoadError(null);
            try {
                await loadSmartCaptchaScript();
                if (cancelled || !containerRef.current || !window.smartCaptcha) {
                    return;
                }

                if (widgetIdRef.current != null && window.smartCaptcha.destroy) {
                    try {
                        window.smartCaptcha.destroy(widgetIdRef.current);
                    } catch {
                        // ignore destroy errors before re-render
                    }
                }

                containerRef.current.innerHTML = '';
                widgetIdRef.current = window.smartCaptcha.render(containerRef.current, {
                    sitekey: siteKey,
                    callback: (token) => onTokenRef.current?.(token),
                });
            } catch (error) {
                if (!cancelled) {
                    setLoadError(error.message || 'Captcha load failed');
                    onTokenRef.current?.('');
                }
            }
        };

        const timerId = window.setTimeout(renderCaptcha, 0);

        return () => {
            cancelled = true;
            window.clearTimeout(timerId);
            if (widgetIdRef.current != null && window.smartCaptcha?.destroy) {
                try {
                    window.smartCaptcha.destroy(widgetIdRef.current);
                } catch {
                    // ignore destroy errors on unmount
                }
            }
            widgetIdRef.current = null;
        };
    }, [enabled, resetKey, siteKey, isValidClientKey]);

    if (!enabled) {
        return null;
    }

    if (!siteKey) {
        if (process.env.NODE_ENV === 'development') {
            return (
                <Typography sx={{ fontSize: 14, color: '#6C7185', textAlign: 'center' }}>
                    {t('Капча не настроена (dev-режим)')}
                </Typography>
            );
        }
        return <Box sx={authErrorSx}>Captcha is not configured</Box>;
    }

    if (!isValidClientKey) {
        return (
            <Typography sx={{ fontSize: 14, color: '#6C7185', textAlign: 'center' }}>
                {t('Неверный client key: нужен ключ с префиксом ysc1_ из Yandex SmartCaptcha')}
            </Typography>
        );
    }

    if (loadError) {
        return <Box sx={authErrorSx}>{loadError}</Box>;
    }

    return <Box ref={containerRef} sx={{ minHeight: 100, display: 'flex', justifyContent: 'center', width: '100%' }} />;
};

export default SmartCaptcha;
